import { useCallback, useMemo } from 'react';
import { ListBox, Selection } from 'react-aria-components';
import { css } from '@emotion/react';
import type { ComponentDocument } from 'common/stack/ComponentDocuments';
import { parsePackageUrl } from 'common/stack/stackUtils';
import { Empty } from 'client/components/Empty';
import { Help } from 'client/components/Help';
import { Stack, type StackProps } from 'client/components/Stack';
import { StackComponentSelectItem } from './StackComponentSelectItem';

export interface StackComponentSelectProps extends Partial<Omit<StackProps, 'value' | 'onChange'>> {
  tab: 'stacks' | 'components';
  stack: { identifier: string; package: string }[];
  components: ComponentDocument[];
  onAdd: (component: { identifier: string; package: string; type: 'component'; details: ComponentDocument }) => void;
  onRemove: (identifier: string) => void;
}

export const StackComponentSelect = ({ tab, stack, components: componentsProp, onAdd, onRemove, ...other }: StackComponentSelectProps) => {
  const components = useMemo(() => componentsProp.filter((component) => component.type === 'component'), [componentsProp]);

  const selectedKeys = new Set(stack.map((component) => parsePackageUrl(component.package).url));

  const handleChange = useCallback(
    (keys: Selection) => {
      if (keys === 'all') return;

      const currentKeySet = selectedKeys;
      const upcomingKeySet = new Set(keys);
      const addedKeys = upcomingKeySet.difference(currentKeySet);
      const removedKeys = currentKeySet.difference(upcomingKeySet);

      for (const key of addedKeys) {
        const details = components.find((component) => component.url === key.toString());
        if (!details) continue;

        onAdd({
          identifier: details.identifier, // FIXME: Make sure this is unique
          package: `${details.url}#${[...details.versions].pop()}`,
          type: 'component',
          details,
        });
      }

      for (const key of removedKeys) {
        onRemove(key.toString());
      }
    },
    [components, onAdd, onRemove, selectedKeys]
  );

  return (
    <Stack gap={0} {...other}>
      {tab === 'stacks' && (
        <Empty subtitle="This listing is currently empty." css={emptyStyle} />
        // <ListBox selectionMode="single" selectedKeys={selectedKeys} onSelectionChange={handleChange} css={selectStyle}></ListBox>
      )}

      {tab === 'components' && (
        <ListBox selectionMode="multiple" selectedKeys={selectedKeys} onSelectionChange={handleChange} css={selectStyle}>
          {components.map((item) => (
            <StackComponentSelectItem key={item.componentID} {...item} />
          ))}
        </ListBox>
      )}

      <Help>You can select as many as you wish.</Help>
    </Stack>
  );
};

const selectStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
`;

const emptyStyle = css`
  margin: 32px 0;
`;
